import { mdiCompare, mdiEyeOffOutline, mdiFileAlertOutline, mdiScaleBalance, mdiShieldOutline, mdiTrendingUp, mdiWrench } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { Badge } from '../ui/badge'
import { iconFor, accentFor, subtypeLabel } from '../../hooks/useTicketSubtypes'
import type { TicketSubtype } from '../../api/schema'

/** One suggestion issue as the panels consume it. */
export type SuggestionIssue = {
    uid: string
    title: string
    status: string
    priority: number
    due_date?: string | null
    domain?: string | null
    subtype?: string | null
    target_entity_type?: string | null
    target_entity_uid?: string | null
    properties?: {
        suggestion_code?: string
        level?: string
        cost_amount?: number
        [key: string]: unknown
    } | null
}

/** A code→entry index of the catalogue, as the visual helpers consume it. */
export type SubtypeCatalog = Record<string, TicketSubtype>

/** Familles d'écarts — the spec's six archetypes as the UI groups them
 * (spec 2026-07-17-01). A family is a read-time mapping over
 * suggestion_code: prefixes for the seeded/coming producers, exact codes
 * for the engine's. Unknown codes land in "other". */
export type SuggestionFamily =
    | 'coverage'
    | 'recurrence'
    | 'promise'
    | 'blind_spot'
    | 'drift'
    | 'divergence'
    | 'attestation'
    | 'other'

export function familyOf(s: SuggestionIssue): SuggestionFamily {
    const code = s.properties?.suggestion_code ?? ''
    if (code === 'COVERAGE_CANDIDATE' || code === 'CLAIM_POSITION_OVERDUE') return 'coverage'
    if (code === 'WARRANTY_ATTESTATION_EXPIRING') return 'attestation'
    if (code.startsWith('ECART_PROMESSE')) return 'promise'
    if (code.startsWith('ANGLE_MORT')) return 'blind_spot'
    if (code.startsWith('DERIVE')) return 'drift'
    if (code.startsWith('DIVERGENCE')) return 'divergence'
    if (code.startsWith('ECO_') || s.properties?.aggregate != null) return 'recurrence'
    return 'other'
}

/** `icon` is an MDI path from `@mdi/js` (spec 2026-09-17-07). */
type IconConfig = { icon: string; accent: string }

/** Exact-code overrides first (the overdue-claim notice is leverage, red),
 * then the family carries the visual identity: coverage is the hero (money
 * comes back), attestation is preventive, recurrence is arbitrage. */
export const CODE_CONFIG: Record<string, IconConfig> = {
    CLAIM_POSITION_OVERDUE: { icon: mdiScaleBalance, accent: 'text-red-600 dark:text-red-400' },
}
const FAMILY_CONFIG: Record<SuggestionFamily, IconConfig> = {
    coverage: { icon: mdiShieldOutline, accent: 'text-emerald-600 dark:text-emerald-400' },
    attestation: { icon: mdiFileAlertOutline, accent: 'text-amber-600 dark:text-amber-400' },
    recurrence: { icon: mdiWrench, accent: 'text-sky-600 dark:text-sky-400' },
    promise: { icon: mdiScaleBalance, accent: 'text-violet-600 dark:text-violet-400' },
    blind_spot: { icon: mdiEyeOffOutline, accent: 'text-orange-600 dark:text-orange-400' },
    drift: { icon: mdiTrendingUp, accent: 'text-rose-600 dark:text-rose-400' },
    divergence: { icon: mdiCompare, accent: 'text-cyan-600 dark:text-cyan-400' },
    other: { icon: mdiWrench, accent: 'text-sky-600 dark:text-sky-400' },
}
export const DEFAULT_CONFIG = FAMILY_CONFIG.other

/** Visual identity for a suggestion. Catalogue-driven when the issue carries a
 * subtype and the catalogue is supplied (spec 2026-07-18-01); otherwise falls
 * back to the exact-code / family mapping for legacy or un-subtyped issues. */
export function suggestionConfig(s: SuggestionIssue, catalog?: SubtypeCatalog): IconConfig {
    const entry = catalog && s.subtype ? catalog[s.subtype] : undefined
    if (entry) return { icon: iconFor(entry.icon), accent: accentFor(entry.accent) }
    return CODE_CONFIG[s.properties?.suggestion_code ?? ''] ?? FAMILY_CONFIG[familyOf(s)]
}

/** Human label for a suggestion's subtype, from the catalogue when available. */
export function suggestionLabel(s: SuggestionIssue, catalog: SubtypeCatalog | undefined, lang: string): string | null {
    const entry = catalog && s.subtype ? catalog[s.subtype] : undefined
    return entry ? subtypeLabel(entry, lang) : null
}

export function daysUntil(iso?: string | null): number | null {
    if (!iso) return null
    return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

export function formatAmount(cents?: number): string | null {
    if (cents == null || cents <= 0) return null
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(cents / 100)
}

/** Deadline chip: the deadline IS the product ("il vous reste 7 mois"), so
 * it gets the visual weight — red under 30 days, amber under 90. */
export function DeadlineChip({ dueDate }: { dueDate?: string | null }) {
    const { t } = useTranslation('issues')
    const days = daysUntil(dueDate)
    if (days == null) return null
    const cls =
        days < 30
            ? 'border-red-300 text-red-700 dark:text-red-400'
            : days < 90
              ? 'border-amber-300 text-amber-700 dark:text-amber-400'
              : 'border-border text-muted-foreground'
    return (
        <Badge variant="outline" className={cls} data-testid="suggestion-deadline">
            {days < 0 ? t('suggestion.deadline_passed') : t('suggestion.deadline_in', { count: days })}
        </Badge>
    )
}
